import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormControlLabel, Grid } from '@mui/material';

import { Bar, BarStack, LinePath } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';

import {
  getParams,
  getMonth,
  thousandFormat,
  thousandFormat2,
  getMonths,
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';
import appdata from '../../../../Services/appdata';

const x = (d) => d.month;
let tooltipTimeout;

const BottomChart = ({
  bottomData,
  selectedTopItems,
  selectedBottomItems,
  updateFilter,
  defaultStartMonth,
  defaultMonth,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();
  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [colors, setColors] = useState([]);
  const [barKeys, setBarKeys] = useState([]);
  const [bottomOffset, setBottomOffset] = useState(0);
  const [labelChecked, setLabelChecked] = useState(false);

  const handleResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  useEffect(() => {
    prepareData();
  }, [bottomData, selectedTopItems, bottomOffset]);

  const handlePos = useCallback((event) => {
    const _pos = event.currentTarget.getBoundingClientRect().top + window.scrollY;
    if (bottomOffset !== _pos) {
      setBottomOffset(_pos);
    }
  }, [bottomOffset]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotals = {};
    let newData = [];
    let newTotals = [];
    let newBarKeys = [];
    let newColors = [];
    let fiscalMonths = getMonths(defaultStartMonth);

    if (appdata.getLocalItem('labelChecked') === 'true') {
      setLabelChecked(true);
    }

    fiscalMonths.forEach((month) => {
      dictData[month] = {};
      dictData[month]['month'] = month;
      dictData[month]['value'] = {};
      dictTotals[month] = 0;
    });

    if (bottomData !== undefined) {
      bottomData.forEach((item) => {
        let month = getMonth(item.Date);
        if (dictData[month][item.AccountName] === undefined) {
          dictData[month][item.AccountName] = 0;
        }

        if (dictData[month]['value'][item.AccountName] === undefined) {
          dictData[month]['value'][item.AccountName] = 0;
        }
        dictData[month][item.AccountName] += parseFloat(
          item.DistributionAmount,
        );
        dictData[month]['value'][item.AccountName] += parseFloat(
          item.DistributionAmount,
        );
        dictTotals[month] += parseFloat(item.DistributionAmount);
        if (newBarKeys.indexOf(item.AccountName) === -1) {
          newBarKeys.push(item.AccountName);
        }
      });
    }

    newColors = [
      '#25aae3',
      '#283890',
      '#0f75bd',
      '#92278f',
      '#662e91',
      '#e3242b',
      '#f7941d',
      '#39b54a',
      '#00a99d',
      '#8b4513',
      '#c1272d',
      '#006837',
      '#f15a24',
      '#1a1a8c',
      '#754c24',
      '#d9e021',
    ];

    fiscalMonths.forEach((month) => {
      newData.push(dictData[month]);
      newTotals.push(dictTotals[month]);
    });

    setData(newData);
    setTotals(newTotals);
    setColors(newColors);
    setBarKeys(newBarKeys);
  }, [bottomData, defaultStartMonth]);

  const getColor = useCallback((bar) => {
    const year = bar.key;
    const month = bar.bar.data.month;
    const actual = bar.bar.data.value[year]
      ? bar.bar.data.value[year].CashBalance
      : 0;

    const activeColor = actual < 0 ? negativeActiveColor : positiveActiveColor;
    const disableColor =
      actual < 0 ? negativeDisableColor : positiveDisableColor;

    if (selectedBottomItems.length === 0) {
      return activeColor;
    }
    if (selectedBottomItems.indexOf(month) > -1) return activeColor;

    for (let i = 0; i < selectedBottomItems.length; i++) {
      if (
        selectedBottomItems[i].month === month &&
        selectedBottomItems[i].year === year
      ) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedBottomItems]);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedBottomItems;

    const month = bar.bar.data.month;
    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedBottomItems = selectedBottomItems.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedBottomItems.push({ month: month });
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedBottomItems.splice(index, 1);
      }
    } else {
      _selectedBottomItems = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedBottomItems = [];
        _selectedBars = [];
      } else {
        _selectedBottomItems = [{ month: month }];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);

    updateFilter({
      selectedBottomItems: _selectedBottomItems,
      selectedTopItems: [],
    });
  }, [selectedBars, selectedBottomItems, updateFilter]);

  const handleLabel = useCallback((event, month) => {
    let _selectedBottomItems, _selectedBars;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedBottomItems = selectedBottomItems.slice();

      let index = NaN;
      for (let i = 0; i < selectedBottomItems.length; i++) {
        if (selectedBottomItems[i].month === month) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        _selectedBottomItems.push({ month: month });
      } else {
        _selectedBottomItems.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedBottomItems = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBottomItems.length === 1) {
        _selectedBottomItems = [];
        _selectedBars = [];
      } else {
        _selectedBottomItems = [{ month: month }];
        _selectedBars = [event.target];
      }
    }

    setSelectedBars(_selectedBars);

    updateFilter({
      selectedBottomItems: _selectedBottomItems,
      selectedTopItems: [],
    });
  }, [selectedBars, selectedBottomItems, updateFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });
    setHoverBar(null);
    setSelectedBars([]);

    updateFilter({
      selectedBottomItems: [],
    });
  }, [hoverBar, selectedBars, updateFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {};
    const coords = localPoint(event.target.ownerSVGElement, event);

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);
      data = bar;
    } else {
      data['forecast'] = bar;
    }

    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: coords.y,
      tooltipLeft: coords.x,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  const handleLabelCheck = useCallback((event) => {
    setLabelChecked(event.target.checked);
    appdata.setLocalItem('labelChecked', event.target.checked);
  }, []);

  let xTransform = 0;
  let maxYValue = Math.max(...totals);
  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (parseInt(thousandFormat(maxYValue)[0]) > 7)
      xTransform = 8 * (thousandFormat2(maxYValue).length + 2);
    else xTransform = 7.5 * thousandFormat2(maxYValue).length + 10;
  }

  const width = window.innerWidth;
  const height = 150;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );

  const xScale = scaleBand()
    .domain(data.map(x))
    .rangeRound([0, xMax])
    .padding(0.2);     
  
  const yScale = scaleLinear({
    domain:
      totals.length === 0
        ? [100, 0]
        : [Math.max(0, ...totals) * 1.4, Math.min(0, Math.min(...totals))],
    range: [0, yMax],
  });

  const color = scaleOrdinal({
    domain: labelChecked ? [barKeys[0]] : barKeys,
    range: labelChecked ? [colors[0]] : colors,
  });

  let line_data = [];
  line_data.push({ x: 0, y: yScale(0) });
  line_data.push({ x: xMax, y: yScale(0) });

  let futureLabelx, futureWidth, futureX, pastLabelx;
  let futureCount = 12 - defaultMonth;

  if (futureCount === 12) {
    futureLabelx = (width - xTransform - 16) / 2;
    futureWidth = width - xTransform - 16;
    futureX = xTransform;
  } else {
    futureX = ((width - xTransform - 16) / 12) * (12 - futureCount);
    futureWidth = ((width - xTransform - 16) / 12) * futureCount;
    futureLabelx = futureX + futureWidth / 2 - 20;
    pastLabelx = (width - futureWidth - 16) / 2 - 18;
  }

  return (
    <div
      id="rootPos"
      onMouseMove={handlePos}
      onTouchMove={handlePos}
    >
      <div style={{ maxHeight: `${height + 20}px` }}>
        <div className="subtitle mb-10">
          Distributions&nbsp;&nbsp;&nbsp;
          <FormControlLabel
            id="controllLegend"
            control={
              <Checkbox
                checked={labelChecked}
                onChange={handleLabelCheck}
                color="success"
              />
            }
            label="Show/Hide"
          />
        </div>

        <Grid container style={{ position: 'relative' }}>
          <svg width={width} height={height}>
            <rect
              x={0}
              y={0}
              width={width}
              height={height}
              fill="transparent"
              onClick={deSelectAll}
            />
            <Group transform={`translate(${xTransform}, 10)`}>
              {futureCount > 0 && (
                <Group>
                  <Bar
                    width={futureWidth}
                    height={height}
                    fill="#f3f3f3"
                    top={0}
                    x={futureX}
                    y={0}
                    stroke="black"
                    strokeWidth={0}
                  />
                </Group>
              )}

              {futureCount > 0 ? (
                <Group transform="translate(0, -14)">
                  <text
                    fill="black"
                    dx={futureLabelx}
                    dy={12}
                    fontSize="11px"
                    fontWeight="bold"
                  >
                    Future
                  </text>
                </Group>
              ) : null}
              {12 - futureCount > 0 ? (
                <Group transform="translate(0, -14)">
                  <text
                    fill="black"
                    dx={pastLabelx}
                    dy={12}
                    fontSize="11px"
                    fontWeight="bold"
                  >
                    Past
                  </text>
                </Group>
              ) : null}

              <LinePath
                data={line_data}
                x={(nx) => nx.x}
                y={(nx) => nx.y}
                stroke="#555555"
                strokeDasharray="2,2"
              />

              <AxisLeft
                scale={yScale}
                stroke="black"
                tickStroke="black"
                numTicks={5}
                tickLabelProps={(value, index) => ({
                  fill: 'black',
                  fontSize: 11,
                  textAnchor: 'end',
                  dx: '-0.23em',
                  dy: '0.33em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps} fill="black">
                    ${formattedValue}
                  </text>
                )}
              />

              <BarStack
                data={data}
                keys={barKeys}
                x={x}
                xScale={xScale}
                yScale={yScale}
                color={color}
              >
                {(barStacks) => {
                  return barStacks.map((barStack) => {
                    return barStack.bars.map((bar) => {
                      return (
                        <rect
                          key={`bar-income-${barStack.index}-${bar.index}`}
                          x={bar.x}
                          y={bar.y}
                          height={bar.height}
                          width={bar.width}
                          fill={bar.color}
                          onClick={(event) => handleBar(event, bar)}
                          onMouseLeave={hideTooltipHandler}
                          onMouseMove={(event) =>
                            showTooltipHandler(event, xScale, bar)
                          }
                          onTouchEnd={hideTooltipHandler}
                          onTouchMove={(event) =>
                            showTooltipHandler(event, xScale, bar)
                          }
                        />
                      );
                    });
                  });
                }}
              </BarStack>

              <AxisBottom
                hideTicks={true}
                scale={xScale}
                top={yMax}
                stroke="black"
                tickStroke="black"
                tickLabelProps={(value, index) => ({
                  fontSize: 11,
                  fontFamily: 'Arial',
                  textAnchor: 'middle',
                  dy: '-0.5em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text
                    {...tickProps}
                    fill={
                      selectedBottomItems.find(item => item.month === parseInt(formattedValue))
                        ? activeLabelColor
                        : 'black'
                    }
                    onClick={(event) =>
                      handleLabel(event, parseInt(formattedValue))
                    }
                  >
                    {enMonths[formattedValue]}
                  </text>
                )}
              />
            </Group>
          </svg>

          {tooltipOpen && !labelChecked && (
            <TooltipWithBounds
             
              top={tooltipTop + pageYOffset}
              left={tooltipLeft}
            >
              {tooltipData.isBar ? (
                <div className="bkg-white color-black">
                  <div className="pdv-10">
                    <strong>
                      FY {tooltipData.key} :{' '}
                      {enMonths[x(tooltipData.bar.data)]}
                    </strong>
                  </div>
                  <div className="pdv-10">
                    <strong>
                      Actual:{' '}
                      {thousandFormat2(
                        tooltipData.bar.data.value[tooltipData.key],
                      )}
                    </strong>
                  </div>
                </div>
              ) : null}
            </TooltipWithBounds>
          )}
          <div
            style={{
              paddingTop: 10,
              display: 'flex',
            }}
          >
            {!labelChecked &&
              data.length > 0 &&
              Object.keys(data[0].value).map((key, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: 12,
                  }}
                >
                  {key}:&nbsp;
                  <div
                    style={{
                      width: 10,
                      height: 10,
                      backgroundColor: colors[index],
                    }}
                  ></div>
                  &nbsp;&nbsp;
                </div>
              ))}
          </div>
        </Grid>
      </div>
    </div>
  );
};

BottomChart.propTypes = {
  bottomData: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  selectedBottomItems: PropTypes.array.isRequired,
  updateFilter: PropTypes.func.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
};

export default BottomChart;
